import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import StatusBadge from '../components/common/StatusBadge';
import { dateInputToUTC } from '../utils/datetime';
import LoadingSpinner from '../components/common/LoadingSpinner';
import usePermissions from '../hooks/usePermissions';

const ACTION_LABELS = {
  'settings.pricing_update':    'Pricing updated',
  'settings.safety_update':     'Safety settings updated',
  'settings.support_update':    'Support settings updated',
  'settings.fleet_update':      'Fleet rules updated',
  'settings.car_catalog_sync':  'Car catalog synced',
  'user.status_change':         'User status changed',
  'user.wallet_adjust':         'User wallet adjusted',
  'driver.approve':             'Driver approved',
  'driver.document_verify':     'Document verified',
  'driver.status_change':       'Driver status changed',
  'driver.vehicle_type_update': 'Vehicle type assigned',
  'driver.delete':              'Driver deleted',
  'driver.ledger_adjust':       'Driver ledger adjusted',
  'payment.refund':             'Payment refunded',
  'payout.approve':             'Payout approved',
  'payout.reject':              'Payout rejected',
  'payout.mark_paid':           'Payout marked paid',
  'admin.create':               'Admin created',
  'ride.cancel':                'Ride cancelled',
  'ride.reassign':              'Driver reassigned',
  'ride.force_complete':        'Ride force-completed',
  'ride.refund':                'Ride refunded',
  'zone.create':                'Zone created',
  'zone.update':                'Zone updated',
  'zone.delete':                'Zone deleted',
  'coupon.create':              'Coupon created',
  'coupon.update':              'Coupon updated',
  'coupon.delete':              'Coupon deleted',
  'referral.config_update':     'Referral settings updated',
};

const TARGET_TYPES = ['Driver', 'User', 'Payout', 'Payment', 'Settings', 'Admin', 'Ride', 'ServiceArea', 'Coupon'];
const ALL_ACTIONS = Object.keys(ACTION_LABELS);

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function ExpandedRow({ entry }) {
  return (
    <tr>
      <td colSpan={6} className="bg-gray-50 px-6 py-4 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-medium text-gray-700 mb-1">Request body</p>
            <pre className="bg-white border border-gray-200 rounded p-3 text-xs text-gray-600 overflow-auto max-h-40 whitespace-pre-wrap break-all">
              {entry.requestBody
                ? JSON.stringify(entry.requestBody, null, 2)
                : '(empty)'}
            </pre>
          </div>
          <div className="space-y-2">
            <div>
              <span className="font-medium text-gray-700">HTTP path: </span>
              <span className="text-gray-600 font-mono text-xs">{entry.method} {entry.path}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status code: </span>
              <span className="text-gray-600">{entry.statusCode}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">IP address: </span>
              <span className="text-gray-600 font-mono text-xs">{entry.ip || '—'}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">User agent: </span>
              <span className="text-gray-600 text-xs break-all">{entry.userAgent || '—'}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Admin role at time: </span>
              <span className="text-gray-600 capitalize">{entry.adminRole || '—'}</span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AuditLog() {
  const { can } = usePermissions();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [filterAction, setFilterAction] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('');
  const [filterSuccess, setFilterSuccess] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const canView = can('view_audit_logs');

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (filterAction) params.action = filterAction;
      if (filterTargetType) params.targetType = filterTargetType;
      if (filterSuccess !== '') params.success = filterSuccess;
      if (filterStartDate) params.startDate = dateInputToUTC(filterStartDate, 'start');
      if (filterEndDate)   params.endDate   = dateInputToUTC(filterEndDate,   'end');

      const res = await api.get('/admin/audit-logs', { params });
      const { data, total: t, page: pg, pages: pgs } = res.data.data;
      setEntries(data);
      setTotal(t);
      setPage(pg);
      setPages(pgs);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterTargetType, filterSuccess, filterStartDate, filterEndDate]);

  useEffect(() => {
    if (canView) fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setExpandedId(null);
    fetchLogs(1);
  };

  const handleClearFilters = () => {
    setFilterAction('');
    setFilterTargetType('');
    setFilterSuccess('');
    setFilterStartDate('');
    setFilterEndDate('');
    setExpandedId(null);
  };

  const handlePageChange = (p) => {
    setExpandedId(null);
    fetchLogs(p);
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 text-lg font-medium">Access denied</p>
          <p className="text-gray-400 text-sm mt-1">
            You need the <code className="bg-gray-100 px-1 rounded">view_audit_logs</code> permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Activity Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          Every mutating admin action, including failed attempts.
          {total > 0 && (
            <span className="ml-2 font-medium text-gray-700">{total.toLocaleString()} entries</span>
          )}
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <form onSubmit={handleApplyFilters} className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="text-xs font-medium text-gray-600">Action</label>
            <select
              className="input text-sm"
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
            >
              <option value="">All actions</option>
              {ALL_ACTIONS.map((a) => (
                <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-xs font-medium text-gray-600">Target type</label>
            <select
              className="input text-sm"
              value={filterTargetType}
              onChange={(e) => setFilterTargetType(e.target.value)}
            >
              <option value="">All types</option>
              {TARGET_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[120px]">
            <label className="text-xs font-medium text-gray-600">Outcome</label>
            <select
              className="input text-sm"
              value={filterSuccess}
              onChange={(e) => setFilterSuccess(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Success only</option>
              <option value="false">Failed only</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">From</label>
            <input
              type="date"
              className="input text-sm"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600">To</label>
            <input
              type="date"
              className="input text-sm"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn-primary text-sm">Apply</button>
            <button
              type="button"
              className="btn-secondary text-sm"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <LoadingSpinner />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-400">
            No audit log entries found.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-header">Timestamp</th>
                <th className="table-header">Admin</th>
                <th className="table-header">Action</th>
                <th className="table-header">Target</th>
                <th className="table-header">Outcome</th>
                <th className="table-header w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((entry) => {
                const isExpanded = expandedId === entry._id;
                return (
                  <React.Fragment key={entry._id}>
                    <tr
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : entry._id)}
                    >
                      <td className="table-cell text-xs text-gray-500 whitespace-nowrap">
                        {formatDate(entry.createdAt)}
                      </td>
                      <td className="table-cell">
                        <p className="text-sm font-medium text-gray-800">
                          {entry.admin
                            ? `${entry.admin.firstName} ${entry.admin.lastName}`
                            : (entry.adminEmail || '—')}
                        </p>
                        <p className="text-xs text-gray-400 capitalize">{entry.adminRole}</p>
                      </td>
                      <td className="table-cell">
                        <span className="text-sm text-gray-800">
                          {ACTION_LABELS[entry.action] || entry.action}
                        </span>
                        <p className="text-xs text-gray-400 font-mono">{entry.action}</p>
                      </td>
                      <td className="table-cell">
                        {entry.targetType ? (
                          <>
                            <span className="text-sm text-gray-700">{entry.targetType}</span>
                            {entry.targetId && (
                              <p className="text-xs text-gray-400 font-mono truncate max-w-[140px]">
                                {String(entry.targetId)}
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={entry.success ? 'success' : 'failed'} />
                      </td>
                      <td className="table-cell text-gray-400 text-xs select-none">
                        {isExpanded ? '▲' : '▼'}
                      </td>
                    </tr>
                    {isExpanded && <ExpandedRow entry={entry} />}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {pages} ({total.toLocaleString()} entries)
          </p>
          <div className="flex gap-2">
            <button
              className="btn-secondary text-sm"
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </button>
            <button
              className="btn-secondary text-sm"
              disabled={page >= pages}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
